import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'

const HEADER_MENU_WIDGET = '.elementor-element-a262c40'
const HEADER_BAR = '.elementor-element-5673d93'
const MOBILE_MENU_WRAP = '.elementor-element-a262c40 .wpr-mobile-mega-menu-wrap'

function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [gallerySubOpen, setGallerySubOpen] = useState(false)
  const [servicesSubOpen, setServicesSubOpen] = useState(false)

  const detachWprMobileHandlers = useCallback(() => {
    const $ = window.jQuery
    if (!$) return
    const $widget = $(HEADER_MENU_WIDGET)
    $widget.find('.wpr-mobile-toggle').off()
    $widget.find('.wpr-mobile-nav-menu-container a').off()
  }, [])

  const resetMenuWrapStyles = useCallback(() => {
    document.querySelectorAll(MOBILE_MENU_WRAP).forEach((el) => {
      el.style.removeProperty('display')
    })
  }, [])

  const syncHeaderToolbarHeight = useCallback(() => {
    const headerBar = document.querySelector(HEADER_BAR)
    if (!headerBar) return
    document.documentElement.style.setProperty(
      '--tsr-header-toolbar-height',
      `${headerBar.getBoundingClientRect().height}px`
    )
  }, [])

  const closeMenu = useCallback(() => {
    setMobileMenuOpen(false)
    setGallerySubOpen(false)
    setServicesSubOpen(false)
    resetMenuWrapStyles()
  }, [resetMenuWrapStyles])

  useEffect(() => {
    closeMenu()
    detachWprMobileHandlers()
    const timers = [250, 600, 1200].map((delay) => window.setTimeout(detachWprMobileHandlers, delay))
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [location.pathname, detachWprMobileHandlers, closeMenu])

  useEffect(() => {
    const widget = document.querySelector(HEADER_MENU_WIDGET)
    if (widget) {
      widget.removeAttribute('data-widget_type')
    }
    detachWprMobileHandlers()
  }, [detachWprMobileHandlers])

  useEffect(() => {
    if (mobileMenuOpen) detachWprMobileHandlers()
  }, [mobileMenuOpen, detachWprMobileHandlers])

  useEffect(() => {
    syncHeaderToolbarHeight()
    window.addEventListener('resize', syncHeaderToolbarHeight)
    return () => window.removeEventListener('resize', syncHeaderToolbarHeight)
  }, [syncHeaderToolbarHeight, mobileMenuOpen, location.pathname])

  useEffect(() => {
    if (!mobileMenuOpen) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeMenu()
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [mobileMenuOpen, closeMenu])

  const toggleMobileMenu = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (!mobileMenuOpen) syncHeaderToolbarHeight()
    setMobileMenuOpen((open) => {
      if (open) resetMenuWrapStyles()
      return !open
    })
    setGallerySubOpen(false)
    setServicesSubOpen(false)
  }

  const handleNavClick = useCallback((to) => (event) => {
    event.preventDefault()
    event.stopPropagation()
    closeMenu()
    navigate(to)
  }, [closeMenu, navigate])

  const mobileMenuList = (
    <ul id="mobile-menu-2-a262c40" className="wpr-mobile-nav-menu wpr-mobile-mega-menu">
      <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1209"><Link to="/" className="wpr-mobile-menu-item menu-link" onClick={handleNavClick('/')}>Home</Link></li>
      <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1210"><Link to="/about" className="wpr-mobile-menu-item menu-link" onClick={handleNavClick('/about')}>About</Link></li>
      <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-has-children menu-item-635">
        <Link to="/services" className="wpr-mobile-menu-item menu-link" aria-expanded="false" onClick={(e) => { e.preventDefault(); setServicesSubOpen(!servicesSubOpen); }}>Services<i className="wpr-mobile-sub-icon" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setServicesSubOpen(!servicesSubOpen); }}></i></Link>
        <ul className="sub-menu wpr-mobile-sub-menu" style={{display: servicesSubOpen ? 'block' : 'none'}}>
          <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3840"><Link to="/wedding-events" className="wpr-mobile-sub-menu-item menu-link" onClick={handleNavClick('/wedding-events')}>Wedding Events</Link></li>
          <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3837"><Link to="/corporate-events" className="wpr-mobile-sub-menu-item menu-link" onClick={handleNavClick('/corporate-events')}>Corporate Events</Link></li>
          <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3838"><Link to="/social-events" className="wpr-mobile-sub-menu-item menu-link" onClick={handleNavClick('/social-events')}>Social Events</Link></li>
          <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3836"><Link to="/canteen" className="wpr-mobile-sub-menu-item menu-link" onClick={handleNavClick('/canteen')}>Canteen</Link></li>
        </ul>
      </li>
      <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-2005"><Link to="/menu" className="wpr-mobile-menu-item menu-link" onClick={handleNavClick('/menu')}>Menu</Link></li>
      <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3884"><Link to="/venues" className="wpr-mobile-menu-item menu-link" onClick={handleNavClick('/venues')}>Venues</Link></li>
      <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-2085">
        <Link to="#" className="wpr-mobile-menu-item menu-link" aria-expanded="false" onClick={(e) => { e.preventDefault(); setGallerySubOpen(!gallerySubOpen); }}>Gallery<i className="wpr-mobile-sub-icon" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setGallerySubOpen(!gallerySubOpen); }}></i></Link>
        <ul className="sub-menu wpr-mobile-sub-menu" style={{display: gallerySubOpen ? 'block' : 'none'}}>
          <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1231"><Link to="/image-gallery" className="wpr-mobile-sub-menu-item menu-link" onClick={handleNavClick('/image-gallery')}>Image</Link></li>
          <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-2084"><Link to="/video-gallery" className="wpr-mobile-sub-menu-item menu-link" onClick={handleNavClick('/video-gallery')}>Video</Link></li>
        </ul>
      </li>
      <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1340"><Link to="/contact" className="wpr-mobile-menu-item menu-link" onClick={handleNavClick('/contact')}>Contact</Link></li>
    </ul>
  )

  return (
    <>
    {mobileMenuOpen && createPortal(
      <>
        <button
          type="button"
          className="tsr-mobile-menu-backdrop"
          aria-label="Close navigation menu"
          onClick={closeMenu}
        />
        <div
          className="tsr-mobile-menu-portal elementor-271 elementor-element elementor-element-a262c40 wpr-sub-divider-yes wpr-sub-icon-caret-down"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="wpr-mobile-mega-menu-wrap wpr-mobile-menu-open tsr-mobile-menu-open">
            {mobileMenuList}
          </div>
        </div>
      </>,
      document.body
    )}
    <div className={`ekit-template-content-markup ekit-template-content-header${mobileMenuOpen ? ' tsr-header-menu-open' : ''}`}>
      <div data-elementor-type="wp-post" data-elementor-id="271" className="elementor elementor-271">
        <div className="elementor-element elementor-element-5673d93 e-con-full e-flex wpr-particle-no wpr-jarallax-no wpr-parallax-no wpr-sticky-section-no wpr-column-slider-no wpr-equal-height-no e-con e-parent" data-id="5673d93" data-element_type="container" data-e-type="container" data-settings='{"background_background":"classic"}'>

          {/* Logo */}
          <div className="elementor-element elementor-element-b168dec e-con-full e-flex wpr-particle-no wpr-jarallax-no wpr-parallax-no wpr-sticky-section-no wpr-column-slider-no wpr-equal-height-no e-con e-child" data-id="b168dec" data-element_type="container" data-e-type="container">
            <div className="elementor-element elementor-element-29a9b89 elementor-widget__width-initial elementor-widget elementor-widget-image" data-id="29a9b89" data-element_type="widget" data-e-type="widget" data-widget_type="image.default">
              <div className="elementor-widget-container">
                <Link to="/">
                  <img fetchPriority="high" width="1362" height="1651" src={process.env.PUBLIC_URL + "/assets/images/10/tsr-red-png.png"} className="attachment-full size-full wp-image-4009" alt="TSR Catering Logo" />
                </Link>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="elementor-element elementor-element-0339220 e-con-full e-flex wpr-particle-no wpr-jarallax-no wpr-parallax-no wpr-sticky-section-no wpr-column-slider-no wpr-equal-height-no e-con e-child" data-id="0339220" data-element_type="container" data-e-type="container">
            <div className="elementor-element elementor-element-a262c40 wpr-main-menu-align-right wpr-main-menu-align--tabletcenter wpr-main-menu-align--mobilecenter wpr-pointer-underline wpr-pointer-line-fx wpr-pointer-fx-fade wpr-sub-icon-caret-down wpr-sub-menu-fx-fade wpr-nav-menu-bp-tablet wpr-mobile-menu-display-dropdown wpr-mobile-toggle-v1 wpr-sub-divider-yes elementor-widget elementor-widget-wpr-mega-menu" data-id="a262c40" data-element_type="widget" data-e-type="widget" data-settings='{"menu_layout":"horizontal"}'>
              <div className="elementor-widget-container">

                {/* Desktop menu */}
                <nav className="wpr-nav-menu-container wpr-mega-menu-container wpr-nav-menu-horizontal" data-trigger="hover">
                  <ul id="menu-1-a262c40" className="wpr-nav-menu wpr-mega-menu">
                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1209"><Link to="/" className="wpr-menu-item wpr-pointer-item menu-link"><span>Home</span></Link></li>
                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1210"><Link to="/about" className="wpr-menu-item wpr-pointer-item menu-link"><span>About</span></Link></li>
                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-has-children menu-item-635">
                      <Link to="/services" className="wpr-menu-item wpr-pointer-item menu-link" aria-expanded="false"><span>Services</span><i className="wpr-sub-icon fas" aria-hidden="true"></i></Link>
                      <ul className="sub-menu wpr-sub-menu">
                        <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3840"><Link to="/wedding-events" className="wpr-sub-menu-item menu-link"><span>Wedding Events</span></Link></li>
                        <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3837"><Link to="/corporate-events" className="wpr-sub-menu-item menu-link"><span>Corporate Events</span></Link></li>
                        <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3838"><Link to="/social-events" className="wpr-sub-menu-item menu-link"><span>Social Events</span></Link></li>
                        <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3836"><Link to="/canteen" className="wpr-sub-menu-item menu-link"><span>Canteen</span></Link></li>
                      </ul>
                    </li>
                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-2005"><Link to="/menu" className="wpr-menu-item wpr-pointer-item menu-link"><span>Menu</span></Link></li>
                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-3884"><Link to="/venues" className="wpr-menu-item wpr-pointer-item menu-link"><span>Venues</span></Link></li>
                    <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-2085">
                      <Link to="#" className="wpr-menu-item wpr-pointer-item menu-link" aria-expanded="false"><span>Gallery</span><i className="wpr-sub-icon fas" aria-hidden="true"></i></Link>
                      <ul className="sub-menu wpr-sub-menu">
                        <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1231"><Link to="/image-gallery" className="wpr-sub-menu-item menu-link"><span>Image</span></Link></li>
                        <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-2084"><Link to="/video-gallery" className="wpr-sub-menu-item menu-link"><span>Video</span></Link></li>
                      </ul>
                    </li>
                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-1340"><Link to="/contact" className="wpr-menu-item wpr-pointer-item menu-link"><span>Contact</span></Link></li>
                  </ul>
                </nav>

                {/* Mobile menu */}
                <nav className="wpr-mobile-nav-menu-container">
                  <div className="wpr-mobile-toggle-wrap">
                    <button
                      type="button"
                      className={`wpr-mobile-toggle${mobileMenuOpen ? ' tsr-mobile-toggle-active' : ''}`}
                      aria-expanded={mobileMenuOpen}
                      aria-label="Toggle navigation menu"
                      onClick={toggleMobileMenu}
                    >
                      <span className="wpr-mobile-toggle-line"></span>
                      <span className="wpr-mobile-toggle-line"></span>
                      <span className="wpr-mobile-toggle-line"></span>
                    </button>
                  </div>
                </nav>

              </div>
            </div>
          </div>

          {/* Social Icons */}
          <div className="elementor-element elementor-element-27df0cb e-con-full e-flex wpr-particle-no wpr-jarallax-no wpr-parallax-no wpr-sticky-section-no wpr-column-slider-no wpr-equal-height-no e-con e-child" data-id="27df0cb" data-element_type="container" data-e-type="container">
            <div className="elementor-element elementor-element-6f8cd3a elementor-shape-circle elementor-grid-0 e-grid-align-center elementor-widget elementor-widget-social-icons" data-id="6f8cd3a" data-element_type="widget" data-e-type="widget" data-widget_type="social-icons.default">
              <div className="elementor-widget-container">
                <div className="elementor-social-icons-wrapper elementor-grid" role="list">
                  <span className="elementor-grid-item" role="listitem">
                    <a className="elementor-icon elementor-social-icon elementor-social-icon-youtube elementor-animation-grow elementor-repeater-item-b129c1c" href="https://youtube.com/@tsrcateringandevents?feature=shared" target="_blank" rel="noreferrer">
                      <span className="elementor-screen-only">Youtube</span>
                      <svg aria-hidden="true" className="e-font-icon-svg e-fab-youtube" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg"><path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"></path></svg>
                    </a>
                  </span>
                  <span className="elementor-grid-item" role="listitem">
                    <a className="elementor-icon elementor-social-icon elementor-social-icon-instagram elementor-animation-grow elementor-repeater-item-cdaad89" href="https://www.instagram.com/tsrcateringandevents_premium" target="_blank" rel="noreferrer">
                      <span className="elementor-screen-only">Instagram</span>
                      <svg aria-hidden="true" className="e-font-icon-svg e-fab-instagram" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path></svg>
                    </a>
                  </span>
                  <span className="elementor-grid-item" role="listitem">
                    <a className="elementor-icon elementor-social-icon elementor-social-icon-facebook elementor-animation-grow elementor-repeater-item-177f117" href="https://www.facebook.com/share/18cox9K3R8/" target="_blank" rel="noreferrer">
                      <span className="elementor-screen-only">Facebook</span>
                      <svg aria-hidden="true" className="e-font-icon-svg e-fab-facebook" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path></svg>
                    </a>
                  </span>
                  <span className="elementor-grid-item" role="listitem">
                    <a className="elementor-icon elementor-social-icon elementor-social-icon-linkedin elementor-animation-grow elementor-repeater-item-7e09ef3" href="https://www.linkedin.com/in/pratik-tailor-3615a429" target="_blank" rel="noreferrer">
                      <span className="elementor-screen-only">Linkedin</span>
                      <svg aria-hidden="true" className="e-font-icon-svg e-fab-linkedin" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg"><path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path></svg>
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
    </>
  )
}

export default Header
